import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Button,
  Paper,
  Alert,
  Grid,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  TravelExplore as TravelIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,

  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import api from '../config/api';
import {
  THEME_GRADIENTS, 
  THEME_RADIUS,
  THEME_COLORS,
  THEME_BORDERS
} from '../constants/theme';
import { APP_LANGUAGES } from '../constants/languages';

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
];

const REMINDER_TIME_OPTIONS = [
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 360, label: '6 hours before' },
  { value: 720, label: '12 hours before' },
  { value: 1440, label: '1 day before' },
  { value: 2880, label: '2 days before' },
  { value: 4320, label: '3 days before' },
  { value: 10080, label: '1 week before' },
];

const DEFAULT_REMINDER_TIMEZONE = 'America/New_York';

const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
];

function Preferences() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: true,
    darkMode: false,
    locationSharing: false,
    vacationGoal: null,
    destinationType: null,
    tripDuration: null,
    budgetRange: null,
    travelCompanions: null,
    accommodationType: null,
    priority: null,
    travelDistance: null,
    planningStyle: null,
    preferredSeason: 'no_preference',
    preferredCurrency: 'USD',
    preferredLanguage: 'en',
    reminderEnabled: true,
    reminderTimeAdvance: 1440, // 1 day in minutes
    reminderTimezone: DEFAULT_REMINDER_TIMEZONE
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');

  const fetchPreferences = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get('/preferences');
      setPreferences(response.data);
    } catch (err) {
      setError('Failed to fetch preferences');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess('');
    
    try {
      await api.put('/preferences', { preferences });
      
      // Always ensure language is set and persisted
      if (preferences.preferredLanguage) {
        await i18n.changeLanguage(preferences.preferredLanguage);
        localStorage.setItem('preferredLanguage', preferences.preferredLanguage);
        // Update document direction for RTL languages
        document.dir = preferences.preferredLanguage === 'ar' ? 'rtl' : 'ltr';
      }
      
      setSuccess('Preferences updated successfully! Redirecting...');
      
      // Force a full page reload to ensure all components pick up the new language
      setTimeout(() => {
        window.location.href = '/trips';
      }, 1500);
    } catch (err) {
      setError('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  }, [preferences, i18n]);

  const handleToggle = useCallback((name) => (event) => {
    setPreferences((prev) => ({
      ...prev,
      [name]: event.target.checked,
    }));
  }, []);

  const handleSelectChange = useCallback((name) => (event) => {
    const value = event.target.value;
    setPreferences((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Immediately change language when selected (for preview)
    if (name === 'preferredLanguage' && value) {
      i18n.changeLanguage(value);
      // Update document direction for RTL languages
      document.dir = value === 'ar' ? 'rtl' : 'ltr';
    }
  }, [i18n]);

  const handleSkip = useCallback(() => {
    navigate('/trips');
  }, [navigate]);

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          <Typography>{t('preferences.loadingPreferences')}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          background: THEME_GRADIENTS.primary,
          color: 'white',
          borderRadius: THEME_RADIUS.small,
          mb: 4,
          position: 'relative'
        }}
      >
        <Box sx={{ p: { xs: 3, md: 5 }, minHeight: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton 
              onClick={() => navigate('/trips')}
              sx={{ 
                mr: 2,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
              {t('preferences.title')}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '1.1rem', color: 'white' }}>
                          {t('preferences.description')}
          </Typography>
        </Box>
      </Paper>

      {/* Main Form */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: THEME_RADIUS.small,
          border: THEME_BORDERS.light,
          overflow: 'hidden'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Left Column - Form Fields */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  {/* Notifications Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('preferences.notifications')}
                    </Typography>
                    
                    <Card 
                      sx={{ 
                        mb: 3,
                        borderRadius: THEME_RADIUS.small,
                        border: THEME_BORDERS.light,
                        boxShadow: 'none'
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: THEME_RADIUS.small,
                              bgcolor: `${THEME_COLORS.primary.main}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <NotificationsIcon sx={{ fontSize: 20, color: THEME_COLORS.primary.main }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('preferences.notificationsDesc')}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, sm: 2 } }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={preferences.emailNotifications}
                                onChange={handleToggle('emailNotifications')}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: THEME_COLORS.primary.main,
                                    '&:hover': {
                                      backgroundColor: `${THEME_COLORS.primary.main}14`,
                                    },
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: THEME_COLORS.primary.main,
                                  },
                                  '& .MuiSwitch-track': {
                                    backgroundColor: '#e0e0e0',
                                  },
                                  '& .MuiSwitch-thumb': {
                                    boxShadow: '0 2px 4px 0 rgba(0,35,11,0.2)',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ 
                                ml: { xs: 1.5, sm: 1 },
                                cursor: 'pointer',
                                minHeight: { xs: 48, sm: 40 },
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <EmailIcon sx={{ 
                                    fontSize: { xs: 18, sm: 16 }, 
                                    color: THEME_COLORS.text.secondary 
                                  }} />
                                  <Typography variant="body1" sx={{ 
                                    fontWeight: 500,
                                    fontSize: { xs: '1rem', sm: '0.875rem' }
                                  }}>
                                    {t('preferences.emailNotifications')}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: { xs: '0.875rem', sm: '0.875rem' }
                                }}>
                                  {t('preferences.emailNotificationsDesc')}
                                </Typography>
                              </Box>
                            }
                            sx={{ 
                              alignItems: 'flex-start',
                              m: 0,
                              width: '100%',
                              '& .MuiFormControlLabel-label': {
                                width: '100%'
                              }
                            }}
                          />
                          
                          <FormControlLabel
                            control={
                              <Switch
                                checked={preferences.pushNotifications}
                                onChange={handleToggle('pushNotifications')}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: THEME_COLORS.primary.main,
                                    '&:hover': {
                                      backgroundColor: `${THEME_COLORS.primary.main}14`,
                                    },
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: THEME_COLORS.primary.main,
                                  },
                                  '& .MuiSwitch-track': {
                                    backgroundColor: '#e0e0e0',
                                  },
                                  '& .MuiSwitch-thumb': {
                                    boxShadow: '0 2px 4px 0 rgba(0,35,11,0.2)',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ 
                                ml: { xs: 1.5, sm: 1 },
                                cursor: 'pointer',
                                minHeight: { xs: 48, sm: 40 },
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <PhoneIcon sx={{ 
                                    fontSize: { xs: 18, sm: 16 }, 
                                    color: THEME_COLORS.text.secondary 
                                  }} />
                                  <Typography variant="body1" sx={{ 
                                    fontWeight: 500,
                                    fontSize: { xs: '1rem', sm: '0.875rem' }
                                  }}>
                                    {t('preferences.pushNotifications')}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: { xs: '0.875rem', sm: '0.875rem' }
                                }}>
                                  {t('preferences.pushNotificationsDesc')}
                                </Typography>
                              </Box>
                            }
                            sx={{ 
                              alignItems: 'flex-start',
                              m: 0,
                              width: '100%',
                              '& .MuiFormControlLabel-label': {
                                width: '100%'
                              }
                            }}
                          />

                          <FormControlLabel
                            control={
                              <Switch
                                checked={preferences.locationSharing}
                                onChange={handleToggle('locationSharing')}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: THEME_COLORS.primary.main,
                                    '&:hover': {
                                      backgroundColor: `${THEME_COLORS.primary.main}14`,
                                    },
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: THEME_COLORS.primary.main,
                                  },
                                  '& .MuiSwitch-track': {
                                    backgroundColor: '#e0e0e0',
                                  },
                                  '& .MuiSwitch-thumb': {
                                    boxShadow: '0 2px 4px 0 rgba(0,35,11,0.2)',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ 
                                ml: { xs: 1.5, sm: 1 },
                                cursor: 'pointer',
                                minHeight: { xs: 48, sm: 40 },
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <LocationIcon sx={{ 
                                    fontSize: { xs: 18, sm: 16 }, 
                                    color: THEME_COLORS.text.secondary 
                                  }} />
                                  <Typography variant="body1" sx={{ 
                                    fontWeight: 500,
                                    fontSize: { xs: '1rem', sm: '0.875rem' }
                                  }}>
                                    {t('preferences.locationSharing')}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{
                                  fontSize: { xs: '0.875rem', sm: '0.875rem' }
                                }}>
                                  {t('preferences.locationSharingDesc')}
                                </Typography>
                              </Box>
                            }
                            sx={{ 
                              alignItems: 'flex-start',
                              m: 0,
                              width: '100%',
                              '& .MuiFormControlLabel-label': {
                                width: '100%'
                              }
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Currency Preferences Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('preferences.currencyExpenses')}
                    </Typography>
                    
                    <Card 
                      sx={{ 
                        mb: 3,
                        borderRadius: THEME_RADIUS.small,
                        border: THEME_BORDERS.light,
                        boxShadow: 'none'
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: THEME_RADIUS.small,
                              bgcolor: `${THEME_COLORS.primary.main}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <Typography sx={{ fontSize: 20 }}>💰</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('preferences.selectCurrency')}
                          </Typography>
                        </Box>

                        <FormControl fullWidth>
                          <InputLabel id="currency-select-label">{t('preferences.preferredCurrency')}</InputLabel>
                          <Select
                            labelId="currency-select-label"
                            value={preferences.preferredCurrency || 'USD'}
                            onChange={handleSelectChange('preferredCurrency')}
                            label={t('preferences.preferredCurrency')}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_BORDERS.light,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary.main,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary.main,
                              }
                            }}
                          >
                            {CURRENCIES.map((currency) => (
                              <MenuItem key={currency.code} value={currency.code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography sx={{ fontSize: '1.2rem', minWidth: 24 }}>
                                    {currency.symbol}
                                  </Typography>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {currency.code}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {currency.name}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Language Preferences Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('preferences.languageRegion')}
                    </Typography>
                    
                    <Card 
                      sx={{ 
                        mb: 3,
                        borderRadius: THEME_RADIUS.small,
                        border: THEME_BORDERS.light,
                        boxShadow: 'none'
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: THEME_RADIUS.small,
                              bgcolor: `${THEME_COLORS.primary.main}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <Typography sx={{ fontSize: 20 }}>🌍</Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('preferences.chooseLanguage')}
                          </Typography>
                        </Box>

                        <FormControl fullWidth>
                          <InputLabel id="language-select-label">{t('preferences.preferredLanguage')}</InputLabel>
                          <Select
                            labelId="language-select-label"
                            value={preferences.preferredLanguage || 'en'}
                            onChange={handleSelectChange('preferredLanguage')}
                            label={t('preferences.preferredLanguage')}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_BORDERS.light,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary.main,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary.main,
                              }
                            }}
                          >
                            {APP_LANGUAGES.map((language) => (
                              <MenuItem key={language.code} value={language.code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Typography sx={{ fontSize: '1.2rem', minWidth: 32 }}>
                                    {language.flag}
                                  </Typography>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {language.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {language.nativeName}
                                    </Typography>
                                  </Box>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Reminder Preferences Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('preferences.reminders')}
                    </Typography>
                    
                    <Card 
                      sx={{ 
                        mb: 3,
                        borderRadius: THEME_RADIUS.small,
                        border: THEME_BORDERS.light,
                        boxShadow: 'none'
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: THEME_RADIUS.small,
                              bgcolor: `${THEME_COLORS.accent.main}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <ScheduleIcon sx={{ fontSize: 20, color: THEME_COLORS.accent.main }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('preferences.remindersDesc')}
                          </Typography>
                        </Box>

                        {/* Enable/Disable Reminders */}
                        <Box sx={{ mb: 3 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={preferences.reminderEnabled}
                                onChange={handleToggle('reminderEnabled')}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: THEME_COLORS.accent.main,
                                    '&:hover': {
                                      backgroundColor: `${THEME_COLORS.accent.main}14`,
                                    },
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: THEME_COLORS.accent.main,
                                  },
                                  '& .MuiSwitch-track': {
                                    backgroundColor: '#e0e0e0',
                                  },
                                  '& .MuiSwitch-thumb': {
                                    boxShadow: '0 2px 4px 0 rgba(0,35,11,0.2)',
                                  },
                                }}
                              />
                            }
                            label={
                              <Box sx={{ 
                                ml: 1,
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <AccessTimeIcon sx={{ 
                                    fontSize: 16, 
                                    color: THEME_COLORS.text.secondary 
                                  }} />
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {t('preferences.enableReminders')}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {t('preferences.enableRemindersDesc')}
                                </Typography>
                              </Box>
                            }
                            sx={{ 
                              alignItems: 'flex-start',
                              m: 0,
                              width: '100%',
                              '& .MuiFormControlLabel-label': {
                                width: '100%'
                              }
                            }}
                          />
                        </Box>

                        {/* Reminder Settings - Only show if reminders are enabled */}
                        {preferences.reminderEnabled && (
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel id="reminder-time-select-label">{t('preferences.reminderTiming')}</InputLabel>
                                <Select
                                  labelId="reminder-time-select-label"
                                  value={preferences.reminderTimeAdvance || 1440}
                                  onChange={handleSelectChange('reminderTimeAdvance')}
                                  label={t('preferences.reminderTiming')}
                                  sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: THEME_BORDERS.light,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: THEME_COLORS.accent.main,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: THEME_COLORS.accent.main,
                                    }
                                  }}
                                >
                                  {REMINDER_TIME_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth>
                                <InputLabel id="timezone-select-label">{t('preferences.yourTimezone')}</InputLabel>
                                <Select
                                  labelId="timezone-select-label"
                                  value={preferences.reminderTimezone || DEFAULT_REMINDER_TIMEZONE}
                                  onChange={handleSelectChange('reminderTimezone')}
                                  label={t('preferences.yourTimezone')}
                                  sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: THEME_BORDERS.light,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: THEME_COLORS.accent.main,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: THEME_COLORS.accent.main,
                                    }
                                  }}
                                >
                                  {TIMEZONE_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>
                        )}

                        {/* Reminder Preview */}
                        {preferences.reminderEnabled && (
                          <Box sx={{ 
                            mt: 3, 
                            p: 2, 
                            bgcolor: `${THEME_COLORS.accent.main}08`,
                            borderRadius: THEME_RADIUS.small,
                            border: `1px solid ${THEME_COLORS.accent.main}20`
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Preview:</strong> You'll receive one daily email summarizing activities about{' '}
                              {(REMINDER_TIME_OPTIONS.find(opt => opt.value === preferences.reminderTimeAdvance)?.label || '1 day before').toLowerCase()}
                              , with times in{' '}
                              {TIMEZONE_OPTIONS.find(opt => opt.value === (preferences.reminderTimezone || DEFAULT_REMINDER_TIMEZONE))?.label || 'Eastern Time (US & Canada)'}.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              💡 You get one itinerary summary per day (not one email per activity). Chat messages can still notify separately.
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Travel Preferences Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('preferences.travelPreferences')}
                    </Typography>
                    
                    <Card 
                      sx={{ 
                        borderRadius: THEME_RADIUS.small,
                        border: THEME_BORDERS.light,
                        boxShadow: 'none'
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: THEME_RADIUS.small,
                              bgcolor: `${THEME_COLORS.secondary.main}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <TravelIcon sx={{ fontSize: 20, color: THEME_COLORS.secondary.main }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('preferences.travelPreferencesDesc')}
                          </Typography>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Preferred Season</InputLabel>
                              <Select
                                value={preferences.preferredSeason || 'no_preference'}
                                onChange={handleSelectChange('preferredSeason')}
                                label="Preferred Season"
                                sx={{
                                  borderRadius: THEME_RADIUS.small,
                                }}
                              >
                                <MenuItem value="no_preference">No Preference</MenuItem>
                                <MenuItem value="spring">Spring</MenuItem>
                                <MenuItem value="summer">Summer</MenuItem>
                                <MenuItem value="fall">Fall</MenuItem>
                                <MenuItem value="winter">Winter</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Vacation Goal</InputLabel>
                              <Select
                                value={preferences.vacationGoal || ''}
                                onChange={handleSelectChange('vacationGoal')}
                                label="Vacation Goal"
                                sx={{
                                  borderRadius: THEME_RADIUS.small,
                                }}
                              >
                                <MenuItem value="">Select...</MenuItem>
                                <MenuItem value="relaxation">Relaxation</MenuItem>
                                <MenuItem value="adventure">Adventure</MenuItem>
                                <MenuItem value="cultural">Culture & History</MenuItem>
                                <MenuItem value="family">Family</MenuItem>
                                <MenuItem value="party">Party</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Budget Range</InputLabel>
                              <Select
                                value={preferences.budgetRange || ''}
                                onChange={handleSelectChange('budgetRange')}
                                label="Budget Range"
                                sx={{
                                  borderRadius: THEME_RADIUS.small,
                                }}
                              >
                                <MenuItem value="">Select...</MenuItem>
                                <MenuItem value="under_500">Under $500</MenuItem>
                                <MenuItem value="500-1000">$500 - $1000</MenuItem>
                                <MenuItem value="1000-2000">$1000 - $2000</MenuItem>
                                <MenuItem value="over_2000">Over $2000</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Travel Companions</InputLabel>
                              <Select
                                value={preferences.travelCompanions || ''}
                                onChange={handleSelectChange('travelCompanions')}
                                label="Travel Companions"
                                sx={{
                                  borderRadius: THEME_RADIUS.small,
                                }}
                              >
                                <MenuItem value="">Select...</MenuItem>
                                <MenuItem value="solo">Solo</MenuItem>
                                <MenuItem value="partner">Partner</MenuItem>
                                <MenuItem value="family">Family</MenuItem>
                                <MenuItem value="friends">Friends</MenuItem>
                                <MenuItem value="group">Group</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* Right Column - Settings Preview */}
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    bgcolor: THEME_COLORS.background.light,
                    borderRadius: THEME_RADIUS.small,
                    border: THEME_BORDERS.light,
                    height: 'fit-content'
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    {t('preferences.settingsPreview')}
                  </Typography>
                  
                  {/* Notification Settings */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Notifications
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: 16, color: preferences.emailNotifications ? THEME_COLORS.primary.main : THEME_COLORS.text.secondary }} />
                        <Typography variant="body2" sx={{ 
                          color: preferences.emailNotifications ? THEME_COLORS.text.primary : THEME_COLORS.text.secondary,
                          fontWeight: preferences.emailNotifications ? 500 : 400
                        }}>
                          Email: {preferences.emailNotifications ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: preferences.pushNotifications ? THEME_COLORS.primary.main : THEME_COLORS.text.secondary }} />
                        <Typography variant="body2" sx={{ 
                          color: preferences.pushNotifications ? THEME_COLORS.text.primary : THEME_COLORS.text.secondary,
                          fontWeight: preferences.pushNotifications ? 500 : 400
                        }}>
                          Push: {preferences.pushNotifications ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon sx={{ fontSize: 16, color: preferences.locationSharing ? THEME_COLORS.primary.main : THEME_COLORS.text.secondary }} />
                        <Typography variant="body2" sx={{ 
                          color: preferences.locationSharing ? THEME_COLORS.text.primary : THEME_COLORS.text.secondary,
                          fontWeight: preferences.locationSharing ? 500 : 400
                        }}>
                          Location: {preferences.locationSharing ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Currency Settings */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Currency
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 16 }}>💰</Typography>
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                        {CURRENCIES.find(c => c.code === preferences.preferredCurrency)?.symbol || '$'} {preferences.preferredCurrency || 'USD'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Language Settings */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Language
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: 16 }}>🌍</Typography>
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                        {APP_LANGUAGES.find(l => l.code === preferences.preferredLanguage)?.name || 'English'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Reminder Settings */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Activity Reminders
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: preferences.reminderEnabled ? THEME_COLORS.accent.main : THEME_COLORS.text.secondary }} />
                        <Typography variant="body2" sx={{ 
                          color: preferences.reminderEnabled ? THEME_COLORS.text.primary : THEME_COLORS.text.secondary,
                          fontWeight: preferences.reminderEnabled ? 500 : 400
                        }}>
                          {preferences.reminderEnabled ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>
                      {preferences.reminderEnabled && (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 2.5 }}>
                            {REMINDER_TIME_OPTIONS.find(opt => opt.value === preferences.reminderTimeAdvance)?.label || '1 day before'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', ml: 2.5 }}>
                            {TIMEZONE_OPTIONS.find(opt => opt.value === (preferences.reminderTimezone || DEFAULT_REMINDER_TIMEZONE))?.label.split(' ')[0] || 'Eastern'}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Travel Preferences */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Travel Style
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Season: {preferences.preferredSeason === 'no_preference' ? 'Any' : preferences.preferredSeason || 'Not set'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Goal: {preferences.vacationGoal || 'Not set'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Budget: {preferences.budgetRange || 'Not set'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Companions: {preferences.travelCompanions || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Summary */}
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: THEME_COLORS.background.paper,
                    borderRadius: THEME_RADIUS.small,
                    border: THEME_BORDERS.light
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Your preferences will be saved and applied across all devices
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              pt: 4,
              mt: 4,
              borderTop: THEME_BORDERS.light,
              position: { xs: 'fixed', md: 'static' },
              bottom: { xs: 0 },
              left: { xs: 0 },
              right: { xs: 0 },
              bgcolor: { xs: 'rgba(255, 255, 255, 0.95)' },
              backdropFilter: { xs: 'blur(8px)' },
              zIndex: { xs: 1000 },
              px: { xs: 2, md: 0 },
              py: { xs: 2, md: 0 },
              boxShadow: { xs: '0 -2px 10px rgba(0, 0, 0, 0.08)', md: 'none' },
              gap: 2
            }}>
              <Button
                onClick={handleSkip}
                size="large"
                sx={{ 
                  borderRadius: THEME_RADIUS.large,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '1.1rem', md: '1rem' },
                  py: 2,
                  px: 4,
                  minWidth: 120,
                  boxShadow: 'none',
                }}
              >
                {t('preferences.cancel')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={saving}
                sx={{ 
                  borderRadius: THEME_RADIUS.large,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', md: '1rem' },
                  py: 2,
                  px: 4,
                  minWidth: 120,
                  backgroundColor: THEME_COLORS.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: THEME_COLORS.primary.dark,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    backgroundColor: THEME_COLORS.text.disabled,
                    color: 'white',
                    opacity: 0.6
                  },
                  boxShadow: 'none',
                }}
              >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>{t('preferences.save')}</>
                )}
              </Button>
            </Box>

            {/* Add bottom padding on mobile to prevent content from being hidden behind fixed bar */}
            <Box sx={{ 
              display: { xs: 'block', md: 'none' },
              height: '100px' // Adjust this value based on the actual height of your action bar
            }} />

            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: THEME_RADIUS.small }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mt: 3, borderRadius: THEME_RADIUS.small }}>
                {success}
              </Alert>
            )}
          </Box>
        </form>
      </Paper>
    </Container>
  );
}

export default Preferences; 